/**
 * Copy for Go's send guards (discovr #742): the address holds an admin can override, the reasons a
 * mailbox paused itself, the sending domain's Postmaster grade, and the two admin alerts about them.
 * Go stores machine reasons such as `policy_breaker: policy_rejections_7d=2`; this is the one place
 * they become words. A reason this file can't read is shown as it is, never dropped.
 */

export const ADDRESS_GUARD_REASONS = ['no_mx', 'role_address', 'disposable_address'] as const;
export type AddressGuardReason = (typeof ADDRESS_GUARD_REASONS)[number];

/** The holds `override_address_guard` releases (Go IsAddressGuardReason). */
export function isAddressGuardReason(code: unknown): code is AddressGuardReason {
  return typeof code === 'string' && (ADDRESS_GUARD_REASONS as readonly string[]).includes(code);
}

const ADDRESS_GUARD_RISK: Record<AddressGuardReason, string> = {
  no_mx: "This address's domain doesn't accept email, so it will almost certainly bounce. Bounces count against the sending mailbox and can get it paused.",
  role_address: 'This is a shared inbox (such as noreply@ or postmaster@), not a person. Cold email to shared inboxes draws more spam reports, which hurt the sending domain.',
  disposable_address: 'This is a disposable address that may already have stopped working. Mail to it often bounces or is never read.',
};

/** Why the address was held, as the end of "held because …". */
const ADDRESS_GUARD_BECAUSE: Record<AddressGuardReason, string> = {
  no_mx: "the address's domain doesn't accept email",
  role_address: 'it goes to a shared inbox (such as noreply@), not a person',
  disposable_address: 'it goes to a disposable address',
};

export interface AddressGuardConfirmCopy {
  title: string;
  description: string;
  confirmLabel: string;
}

/** The Send anyway confirm: the address, the risk in plain words, and when it goes out. */
export function addressGuardConfirm(code: string, opts: { scheduled?: boolean; to?: string } = {}): AddressGuardConfirmCopy {
  const risk = isAddressGuardReason(code) ? ADDRESS_GUARD_RISK[code] : 'We flagged this address as risky to email.';
  const when = opts.scheduled
    ? "it is released to send now, not at the scheduled time (the mailbox's pacing and daily limit still apply)."
    : "it is released to send now (the mailbox's pacing and daily limit still apply).";
  return {
    title: 'Send anyway?',
    description: `${opts.to ? `It goes to ${opts.to}. ` : ''}${risk} If you send it anyway, ${when} The override is recorded in the activity log.`,
    confirmLabel: 'Send anyway',
  };
}

/** ADDRESS_GUARD_OVERRIDE_REQUIRED's copy: Go names the hold in `details.held_reason`. */
export function addressGuardHeldCopy(code: unknown): string {
  const because = isAddressGuardReason(code) ? ADDRESS_GUARD_BECAUSE[code] : 'we flagged its address';
  return `This first email is held because ${because}. An admin can send it anyway, or you can change the creator's email.`;
}

/** Whether the viewer may use Send anyway on a risky address: admin tier only (owner ruling 2026-10-12). Absent reads as no. */
export interface SendAnywayAccess {
  canSendAnyway?: boolean;
}

/** A held risky-address message, read by someone who can't send it anyway. */
export const ADDRESS_GUARD_ADMIN_ONLY_NOTE = "Held for a risky address. Only an admin can send it anyway, or you can change the creator's email.";

/** A flagged first-touch draft, read in the thread: Bulk send approves it and Go holds it; an admin sends the held email anyway from its thread. */
export function addressGuardDraftNote(code: unknown, { canSendAnyway = false }: SendAnywayAccess = {}): string {
  const because = isAddressGuardReason(code) ? ADDRESS_GUARD_BECAUSE[code] : 'we flagged its address';
  const next = canSendAnyway ? 'Once it is held, use Send anyway on it here to send it.' : 'Only an admin can send it anyway.';
  return `Risky address: ${because}. Approved with the others, it will be held. ${next}`;
}

/** The same flag on the wizard's Review. */
export function addressGuardReviewNote(code: unknown): string {
  const because = isAddressGuardReason(code) ? ADDRESS_GUARD_BECAUSE[code] : 'we flagged its address';
  return `Risky address: ${because}. Sent with the others, it will be held, not sent.`;
}

/** Review's count line, stated before Send (an operator must not learn about holds only afterwards). */
export function riskyAddressCountLine(n: number, { canSendAnyway = false }: SendAnywayAccess = {}): string {
  if (!canSendAnyway) {
    return n === 1
      ? '1 email goes to a risky address and will be held, not sent. Only an admin can send it anyway.'
      : `${n} emails go to a risky address and will be held, not sent. Only an admin can send them anyway.`;
  }
  return n === 1
    ? '1 email goes to a risky address and will be held, not sent. Once it is held, open the creator to send it anyway.'
    : `${n} emails go to a risky address and will be held, not sent. Once they are held, open each creator to send it anyway.`;
}

export const ALL_HELD_TITLE = 'Held, not sent';
export const ALL_HELD_SUBTITLE = 'Every email in this send goes to a risky address, so all of them are held. An admin can open each creator to send it anyway.';

/** The confirmation's line for approved emails Go then held at send time. */
export function riskyAddressHeldLine(n: number, usernames: string[]): string {
  const who = usernames.length > 0 ? `: ${usernames.map((u) => `@${u}`).join(', ')}${n > usernames.length ? ` and ${n - usernames.length} more` : ''}` : '';
  return `${n} held for a risky address${who}. An admin can open the creator to send ${n === 1 ? 'it' : 'each one'} anyway.`;
}

interface ParsedReason {
  kind: string;
  fields: Record<string, string>;
}

/** `kind: k=v k=v` → its parts; null for free text (an admin's own pause reason). */
function parseReason(reason: string | undefined): ParsedReason | null {
  const match = /^([a-z_]+):\s*(.*)$/.exec(reason?.trim() ?? '');
  if (!match) return null;
  const [, kind = '', rest = ''] = match;
  const fields: Record<string, string> = {};
  for (const token of rest.split(/\s+/)) {
    const eq = token.indexOf('=');
    if (eq > 0) fields[token.slice(0, eq)] = token.slice(eq + 1);
  }
  return { kind, fields };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-11" → "11 Oct", read as a calendar day (no time zone shift). */
function calendarDay(iso: string | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  if (!match) return null;
  const month = MONTHS[Number(match[2]) - 1];
  return month ? `${Number(match[3])} ${month}` : null;
}

const titleCase = (word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

/** "spam reports 0.35%, reputation Bad" from a Postmaster reason's fields. */
function postmasterSignals(fields: Record<string, string>): string | null {
  const parts = [
    fields.spam_ratio ? `spam reports ${fields.spam_ratio}` : null,
    fields.domain_reputation ? `reputation ${titleCase(fields.domain_reputation)}` : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(', ') : null;
}

interface PauseCause {
  /** What happened, as a clause after "paused automatically: ". */
  what: string;
  /** What to do, on the Mailboxes page itself. */
  next: string;
  /** What to do, from an alert that links to that page. */
  alertNext: string;
}

const MAILBOXES_PAGE = 'Settings › Mailboxes';

function pauseCause(reason: string | undefined): PauseCause | null {
  const parsed = parseReason(reason);
  if (!parsed) return null;
  const { kind, fields } = parsed;
  if (kind === 'policy_breaker' && fields.policy_rejections_7d) {
    const domains = fields.policy_recipient_domains_7d ? ` to ${fields.policy_recipient_domains_7d} recipient domains` : '';
    return {
      what: `${fields.policy_rejections_7d} emails${domains} were refused as policy violations (5.7.x) in the last 7 days`,
      next: 'Find out why before you resume it.',
      alertNext: `Find out why, then resume it from ${MAILBOXES_PAGE}.`,
    };
  }
  if (kind === 'bounce_breaker' && fields.hard_bounces_7d && fields.sends_7d) {
    const rate = fields.rate ? ` (${fields.rate})` : '';
    return {
      what: `${fields.hard_bounces_7d} of ${fields.sends_7d} emails hard-bounced in the last 7 days${rate}`,
      next: 'Check the addresses before you resume it.',
      alertNext: `Check the addresses, then resume it from ${MAILBOXES_PAGE}.`,
    };
  }
  if (kind === 'postmaster_hold') {
    const signals = postmasterSignals(fields);
    const day = calendarDay(fields.traffic_date);
    if (!signals && !day) return null;
    return {
      what: `Google Postmaster Tools graded the sending domain as risky${day ? ` for ${day}` : ''}${signals ? ` (${signals})` : ''}`,
      next: 'Resume it once the reputation recovers.',
      alertNext: `Resume it from ${MAILBOXES_PAGE} once the reputation recovers.`,
    };
  }
  return null;
}

/** A mailbox's `paused_reason` for Settings › Mailboxes: a sentence for an automatic pause, an admin's own words as they are. */
export function mailboxPauseReasonCopy(reason: string | undefined): string {
  const cause = pauseCause(reason);
  if (!cause) return reason?.trim() ?? '';
  return `Paused automatically: ${cause.what}. ${cause.next}`;
}

export type DomainGuardLevel = 'ok' | 'warn' | 'hold';

export interface DomainGuardCopy {
  level: DomainGuardLevel | (string & {});
  label: string;
  explanation: string;
}

/** Settings › Mailboxes: the sending domain's latest Postmaster grade, or null (no pill) when Postmaster has never graded it. */
export function domainGuardCopy(level: string | undefined, reason: string | undefined, recovery?: { recovering?: boolean; capFactor?: number }): DomainGuardCopy | null {
  const signals = postmasterSignals(parseReason(reason)?.fields ?? {});
  const detail = signals ? ` (${signals})` : '';
  switch (level ?? '') {
    case '':
      return null;
    case 'ok':
      if (recovery?.recovering) {
        const at = recovery.capFactor === undefined ? 'a reduced share' : `${recovery.capFactor.toFixed(2)}`;
        return {
          level: 'recovering',
          label: 'Recovering',
          explanation: `The sending domain is graded OK again. Sending is at ${at} of each mailbox's daily cap and climbs back to full as clean days pass. Warm-up stays paused until then.`,
        };
      }
      return { level: 'ok', label: 'Domain OK', explanation: 'No reputation problem is reported for this sending domain.' };
    case 'warn':
      return {
        level: 'warn',
        label: 'Domain warning',
        explanation: `Google Postmaster Tools flagged this sending domain${detail}. Sending continues at a reduced daily cap (today's cap shows it) and warm-up is paused.`,
      };
    case 'hold':
      return {
        level: 'hold',
        label: 'Domain on hold',
        explanation: `Google Postmaster Tools graded this sending domain as risky${detail}. Its mailboxes were paused. Resume them once the reputation recovers.`,
      };
    default:
      return { level: level as string, label: level as string, explanation: reason ?? '' };
  }
}

export interface OutreachAlertInput {
  type?: string;
  /** The panel's notification payload (Go `metadata`). */
  metadata?: Record<string, unknown>;
  /** Go's sentence: the panel's `message`, or the SSE toast's `body`, which carries no metadata. */
  body?: string;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

function pausedAlert(input: OutreachAlertInput): string | null {
  let address = str(input.metadata?.address);
  let reason = str(input.metadata?.reason);
  if (!address || !reason) {
    const match = /^(\S+) was paused: (.*)\. Resume it from Settings/.exec(input.body ?? '');
    if (!match) return null;
    [, address, reason] = match;
  }
  const cause = pauseCause(reason);
  return cause ? `${address} was paused automatically: ${cause.what}. ${cause.alertNext}` : null;
}

function warnedAlert(input: OutreachAlertInput): string | null {
  let domain = str(input.metadata?.domain);
  let reason = str(input.metadata?.reason);
  const rawFactor = input.metadata?.cap_factor;
  let factor = typeof rawFactor === 'number' ? rawFactor : undefined;
  if (!domain || !reason) {
    const match = /^Google Postmaster Tools graded (\S+): (.*)\. Sending continues at (\d+)% of/.exec(input.body ?? '');
    if (!match) return null;
    domain = match[1];
    reason = match[2];
    factor = Number(match[3]) / 100;
  }
  const signals = postmasterSignals(parseReason(reason)?.fields ?? {});
  const cap =
    factor === undefined || factor >= 1
      ? 'Sending continues at the normal daily cap.'
      : `Each mailbox on it now sends at most ${Math.round(factor * 100)}% of its daily cap.`;
  return `Google Postmaster Tools flagged ${domain}${signals ? ` (${signals})` : ''}. ${cap} Warm-up is paused.`;
}

/** The plain sentence for the two send-guard alerts, or null for any other notification (or one it can't read). */
export function outreachAlertCopy(input: OutreachAlertInput): string | null {
  if (input.type === 'outreach.mailbox_auto_paused') return pausedAlert(input);
  if (input.type === 'outreach.sending_domain_warned') return warnedAlert(input);
  return null;
}
